import React, { ErrorInfo, PropsWithChildren } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  isTearingDown,
  isTeardownAbortedQuery,
  isTeardownNetworkError,
  subscribeToTeardown,
} from 'context/consumer-web-bridge/teardown-signal';
import { DefaulErrorPage } from './DefaultErrorPage';

type BaseErrorBoundaryProps = {
  uuid: string;
  trackEvent: (data: Record<string, unknown>) => void;
  ErrorPage?: React.FC<Partial<{ logout: () => void; error: Error }>>;
  logout: () => void;
  domain: string;
};
type BaseErrorBoundaryState =
  | {
      hasError: false;
      error: undefined;
    }
  | {
      hasError: true;
      error: Error;
      isTeardownAbortedQuery: boolean;
      isTeardownNetworkError: boolean;
    };

export class BaseErrorBoundary extends React.Component<
  PropsWithChildren<BaseErrorBoundaryProps>,
  BaseErrorBoundaryState
> {
  constructor(props: BaseErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  private unsubscribeFromTeardown?: () => void;

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
      isTeardownAbortedQuery: isTeardownAbortedQuery(error),
      isTeardownNetworkError: isTeardownNetworkError(error),
    };
  }

  componentDidMount() {
    this.unsubscribeFromTeardown = subscribeToTeardown(
      this.recoverFromTeardownIfSettled
    );
  }

  componentWillUnmount() {
    this.unsubscribeFromTeardown?.();
  }

  private recoverFromTeardownIfSettled = () => {
    if (
      this.state.hasError &&
      this.state.isTeardownNetworkError &&
      !isTearingDown()
    ) {
      this.setState({ hasError: false, error: undefined });
    }
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!this.state.hasError) return;
    if (this.state.isTeardownAbortedQuery) return;
    console.warn('[ERROR BOUNDRY] ', error, errorInfo);
    this.props.trackEvent({
      object: 'error',
      verb: 'tracked',
      details: {
        errorInfo,
        error,
        message: error?.message,
        errorBoundaryDomain: this.props.domain,
      },
    });
    this.props.trackEvent({
      object: 'error_page',
      verb: 'viewed',
      details: {
        errorInfo,
        error,
        message: error?.message,
        errorBoundaryDomain: this.props.domain,
      },
    });

    const hasCrashed = !this.state.isTeardownNetworkError;
    if (hasCrashed) {
      datadogRum.setViewContextProperty('has_crashed', true);
    }
    datadogRum.addError(error, {
      errorInfo,
      uuid: this.props.uuid,
      errorBoundaryDomain: this.props.domain,
      has_crashed: hasCrashed,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isTeardownNetworkError) {
        return null;
      }
      const ErrorPage = this.props.ErrorPage || DefaulErrorPage;
      return <ErrorPage error={this.state.error} logout={this.props.logout} />;
    }

    return <>{this.props.children}</>;
  }
}
